import { ArrowUpRight, Check } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import useReveal from "@/hooks/useReveal";
import { useSiteAssets } from "@/context/SiteAssetsContext";

function ServiceCard({ tag, name, desc, features, image, size, testId }) {
  const { t } = useLang();
  const { ref, visible } = useReveal();
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <article
      ref={ref}
      data-testid={testId}
      className={`reveal ${visible ? "visible" : ""} relative group overflow-hidden bg-neutral-900 text-white ${size}`}
    >
      <div className="absolute inset-0">
        <img
          src={image}
          alt={name}
          className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
      </div>

      <div className="relative z-10 h-full p-6 md:p-8 lg:p-10 flex flex-col justify-between min-h-[360px] md:min-h-[440px]">
        <div className="flex items-start justify-between">
          <span className="font-display font-bold text-white/60 text-sm tracking-widest">{tag}</span>
          <button
            onClick={() => scrollTo("contact")}
            className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300"
            aria-label="contact"
          >
            <ArrowUpRight size={16} />
          </button>
        </div>

        <div>
          <h3 className="font-display font-extrabold text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight mb-3">
            {name}
          </h3>
          <p className="text-sm md:text-base text-white/75 leading-relaxed max-w-lg">{desc}</p>

          <ul className="mt-5 md:mt-6 space-y-2">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-xs md:text-sm text-white/80">
                <Check size={14} className="text-[#1F9D40] shrink-0" strokeWidth={3} />
                {f}
              </li>
            ))}
          </ul>

          <button
            onClick={() => scrollTo("contact")}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-white border-b-2 border-[#1F9D40] pb-1 hover:text-[#1F9D40] transition-colors"
          >
            {t("services.learn_more")}
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Services() {
  const { t } = useLang();
  const { images } = useSiteAssets();
  const s = t("services");

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-28 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-[#E11D2E]" />
              <span className="text-xs uppercase tracking-[0.22em] text-neutral-600 font-semibold">
                {s.eyebrow}
              </span>
            </div>
            <h2 className="font-display font-black text-neutral-900 text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {s.title}
            </h2>
          </div>
          <p className="text-neutral-600 text-base md:text-lg max-w-md leading-relaxed">
            {s.subtitle}
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          <ServiceCard
            testId="service-card-renovation"
            tag={s.items.renovation.tag}
            name={s.items.renovation.name}
            desc={s.items.renovation.desc}
            features={s.items.renovation.features}
            image={images.service_renovation}
            size="md:col-span-8 md:row-span-2"
          />
          <ServiceCard
            testId="service-card-bathroom"
            tag={s.items.bathroom.tag}
            name={s.items.bathroom.name}
            desc={s.items.bathroom.desc}
            features={s.items.bathroom.features}
            image={images.service_bathroom}
            size="md:col-span-4 md:row-span-2"
          />
          <ServiceCard
            testId="service-card-solar"
            tag={s.items.solar_ac.tag}
            name={s.items.solar_ac.name}
            desc={s.items.solar_ac.desc}
            features={s.items.solar_ac.features}
            image={images.service_solar_ac}
            size="md:col-span-4"
          />
          <ServiceCard
            testId="service-card-painting"
            tag={s.items.painting.tag}
            name={s.items.painting.name}
            desc={s.items.painting.desc}
            features={s.items.painting.features}
            image={images.service_painting}
            size="md:col-span-4"
          />
          <ServiceCard
            testId="service-card-ceramica"
            tag={s.items.ceramica.tag}
            name={s.items.ceramica.name}
            desc={s.items.ceramica.desc}
            features={s.items.ceramica.features}
            image={images.service_ceramica}
            size="md:col-span-4"
          />
        </div>
      </div>
    </section>
  );
}
